import { formatMoney } from '../../utils/currency';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Switch, FormControlLabel, Chip, MenuItem, Checkbox,
  Accordion, AccordionSummary, AccordionDetails, Stack, alpha, useTheme, Autocomplete,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import { Add, ExpandMore } from '@mui/icons-material';
import RowActions from '../../components/ui/RowActions';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormDrawer from '../../components/ui/FormDrawer';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import HubServiceTableCell from '../../components/services/HubServiceTableCell';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import { PLATFORM_ROLES } from '../../constants/roleMatrix';
export { default as MasterDataPage } from './MasterDataPage';
export { default as AppMastersPage } from './AppMastersPage';
export { default as AppPromosPage } from './AppPromosPage';
export { default as SettingsPage } from './SettingsPage';
export { default as LocalePage } from '../locale/LocalePage';
export { default as DocumentsPage } from './DocumentsManagementPage';
export { default as RolesPage } from './RolesPrivilegesPage';

export function ServicesPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ _id: string; name: string } | null>(null);
  const [form, setForm] = useState({ name: '', slug: '', serviceCode: 'transportation', description: '', pricing: {} as Record<string, unknown>, isActive: true });
  const { data: platformServices = [] } = useQuery({
    queryKey: ['platform-services'],
    queryFn: async () => {
      const res = await api.get('/platform/services');
      return res.data.data as { code: string; name: string; subServiceTemplates?: { name: string; slug: string; defaultPricing?: Record<string, unknown> }[] }[];
    },
  });
  const { data, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const res = await api.get('/admin/services');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post('/admin/services', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', slug: '', serviceCode: 'transportation', description: '', pricing: {}, isActive: true });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) => api.put(`/admin/services/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      setDialogOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/services/${id}`, { isActive: false }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['services'] }); setDeleteTarget(null); },
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Service',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <HubServiceTableCell
          service={{
            name: row.name as string,
            icon: row.icon as string,
            themeColor: (row.themeColor as string) || '#2563eb',
          }}
        />
      ),
    },
    { field: 'slug', headerName: 'Slug', width: 140 },
    { field: 'hubType', headerName: 'Hub', width: 130 },
    { field: 'serviceCode', headerName: 'Platform Service', width: 140, valueGetter: (_v, row) => row.serviceCode || row.moduleKey },
    {
      field: 'isActive', headerName: 'Active', width: 80,
      renderCell: (params) => <Chip label={params.value ? 'Yes' : 'No'} size="small" color={params.value ? 'success' : 'default'} variant="outlined" />,
    },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (params) => (
        <RowActions
          onEdit={() => {
            setEditing(params.row);
            setForm({
              name: params.row.name,
              slug: params.row.slug,
              serviceCode: params.row.serviceCode || params.row.moduleKey,
              description: params.row.description || '',
              pricing: params.row.pricing || {},
              isActive: params.row.isActive !== false,
            });
            setDialogOpen(true);
          }}
          onDelete={() => setDeleteTarget({ _id: params.row._id, name: params.row.name })}
        />
      ),
    },
  ];

  const applyTemplate = (templateSlug: string) => {
    const platform = platformServices.find((p) => p.code === form.serviceCode);
    const template = platform?.subServiceTemplates?.find((t) => t.slug === templateSlug);
    if (template) {
      setForm((prev) => ({
        ...prev,
        name: template.name,
        slug: template.slug,
        pricing: template.defaultPricing || {},
      }));
    }
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Configuration" title="Tenant Services" subtitle="Sub-services within enabled platform modules — taxi, delivery, etc." actions={<Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialogOpen(true); }}>Add Sub-Service</Button>} />
      <DataTableCard title="Service Catalog">
        <EnhancedDataGrid rows={data?.data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight exportFileName="tenant-services" />
      </DataTableCard>

      <FormDrawer
        open={dialogOpen}
        title={editing ? 'Edit Sub-Service' : 'Create Sub-Service'}
        subtitle="Sub-services within enabled platform modules"
        onClose={() => setDialogOpen(false)}
        onSubmit={() => {
          const payload = { ...form, moduleKey: form.serviceCode, isActive: form.isActive };
          if (editing?._id) updateMutation.mutate({ id: editing._id as string, payload });
          else createMutation.mutate(payload);
        }}
        submitLabel={editing ? 'Update' : 'Create'}
        loading={createMutation.isPending || updateMutation.isPending}
        disableSubmit={!form.name || !form.slug}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth label="Platform Service" value={form.serviceCode}
              onChange={(e) => setForm({ ...form, serviceCode: e.target.value, name: '', slug: '' })}>
              {platformServices.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
            </TextField>
          </Grid>
          {platformServices.find((p) => p.code === form.serviceCode)?.subServiceTemplates?.length ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Quick templates</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {platformServices.find((p) => p.code === form.serviceCode)?.subServiceTemplates?.map((t) => (
                  <Chip key={t.slug} label={t.name} clickable onClick={() => applyTemplate(t.slug)} />
                ))}
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={6}><TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
          <Grid item xs={12}><FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" /></Grid>
        </Grid>
      </FormDrawer>
      <ConfirmDialog open={!!deleteTarget} title="Deactivate service?" message={`Deactivate "${deleteTarget?.name}"?`} onCancel={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)} loading={deleteMutation.isPending} />
    </PageShell>
  );
}

export function FeatureFlagsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const res = await api.get('/admin/feature-flags');
      return res.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (flag: Record<string, unknown>) => api.put('/admin/feature-flags', flag),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feature-flags'] }),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Feature Flags</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Enable or disable features dynamically at client level
      </Typography>
      {(data?.data || []).map((flag: { _id: string; key: string; name: string; enabled: boolean; scope: string }) => (
        <Box key={flag._id} display="flex" justifyContent="space-between" alignItems="center"
          p={2} mb={1} bgcolor="background.paper" borderRadius={2}>
          <Box>
            <Typography fontWeight={600}>{flag.name}</Typography>
            <Typography variant="caption" color="text.secondary">{flag.key} · {flag.scope}</Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={flag.enabled}
                onChange={(e) => toggleMutation.mutate({ ...flag, enabled: e.target.checked })}
              />
            }
            label=""
          />
        </Box>
      ))}
      {!isLoading && !(data?.data?.length) && (
        <Typography color="text.secondary">No feature flags configured. Run seed to populate defaults.</Typography>
      )}
    </Box>
  );
}

export function LegacyRolesPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [permEditing, setPermEditing] = useState<Record<string, unknown> | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [inlinePermissions, setInlinePermissions] = useState<string[]>([]);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '', permissions: '' });
  const [permForm, setPermForm] = useState({ module: 'settings', resource: 'users', action: 'read', description: '' });
  const { data, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const res = await api.get('/admin/roles');
      return res.data;
    },
  });
  const { data: permissionsData = [] } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: async () => {
      const res = await api.get('/admin/permissions');
      return res.data.data as { key: string; module: string; resource: string; action: string }[];
    },
  });
  const groupedPermissions = useMemo(() => {
    const grouped = new Map<string, { key: string; label: string }[]>();
    for (const p of permissionsData) {
      const group = `${p.module}.${p.resource}`;
      const list = grouped.get(group) || [];
      list.push({ key: p.key, label: p.action });
      grouped.set(group, list);
    }
    return Array.from(grouped.entries()).map(([group, permissions]) => ({ group, permissions }));
  }, [permissionsData]);
  const selectedPermissions = useMemo(
    () => new Set(form.permissions.split(',').map((p) => p.trim()).filter(Boolean)),
    [form.permissions]
  );
  const filteredGroups = useMemo(() => {
    const q = permissionSearch.trim().toLowerCase();
    if (!q) return groupedPermissions;
    return groupedPermissions
      .map((g) => ({
        ...g,
        permissions: g.permissions.filter((p) => p.key.toLowerCase().includes(q) || p.label.toLowerCase().includes(q) || g.group.toLowerCase().includes(q)),
      }))
      .filter((g) => g.permissions.length > 0);
  }, [groupedPermissions, permissionSearch]);
  const selectedRole = useMemo(
    () => (data?.data || []).find((r: { _id: string }) => r._id === selectedRoleId),
    [data?.data, selectedRoleId]
  );
  useEffect(() => {
    const roles = (data?.data || []) as { _id: string; permissions: string[] }[];
    if (!roles.length) return;
    const target = roles.find((r) => r._id === selectedRoleId) || roles[0];
    setSelectedRoleId(target._id);
    setInlinePermissions(target.permissions || []);
  }, [data?.data, selectedRoleId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        slug: form.slug,
        description: form.description,
        permissions: form.permissions.split(',').map((p) => p.trim()).filter(Boolean),
      };
      if (editing?._id) return api.put(`/admin/roles/${editing._id as string}`, payload);
      return api.post('/admin/roles', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setOpen(false);
      setEditing(null);
      setForm({ name: '', slug: '', description: '', permissions: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/roles/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });
  const savePermissionMutation = useMutation({
    mutationFn: () => {
      if (permEditing?._id) return api.put(`/admin/permissions/${permEditing._id as string}`, permForm);
      return api.post('/admin/permissions', permForm);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions-catalog'] });
      setPermOpen(false);
      setPermEditing(null);
      setPermForm({ module: 'settings', resource: 'users', action: 'read', description: '' });
    },
  });
  const deletePermissionMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/permissions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['permissions-catalog'] }),
  });
  const inlineSaveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRole?._id) return null;
      return api.put(`/admin/roles/${selectedRole._id as string}`, { permissions: inlinePermissions });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roles'] }),
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Role', flex: 1 },
    { field: 'slug', headerName: 'Slug', width: 150 },
    {
      field: 'permissions', headerName: 'Permissions', flex: 2,
      valueFormatter: (v) => {
        const perms = v as string[];
        return perms.includes('*') ? 'All Permissions' : `${perms.length} permissions`;
      },
    },
    {
      field: 'isSystem', headerName: 'System', width: 80,
      renderCell: (params) => params.value ? 'Yes' : 'No',
    },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (params) => (
        <RowActions
          onEdit={() => {
            setEditing(params.row);
            setForm({
              name: params.row.name,
              slug: params.row.slug,
              description: params.row.description || '',
              permissions: (params.row.permissions || []).join(', '),
            });
            setOpen(true);
          }}
          onDelete={params.row.isSystem ? undefined : () => deleteMutation.mutate(params.row._id)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={700}>Roles & Permissions</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setOpen(true); }}>
          Add Role
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enterprise roles: Super Admin, City Admin, Manager, Dispatcher, Finance, Support
      </Typography>
      <Button size="small" sx={{ mb: 2 }} onClick={() => setShowAdvanced((v) => !v)}>
        {showAdvanced ? 'Hide advanced sections' : 'Show advanced sections'}
      </Button>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(data?.data || []).map((role: { _id: string; name: string; slug: string; permissions: string[]; isSystem?: boolean }) => (
          <Grid item xs={12} sm={6} md={4} key={role._id}>
            <Box sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2.5,
              bgcolor: 'background.paper',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
              outline: selectedRoleId === role._id ? `2px solid ${theme.palette.primary.main}` : 'none',
            }}>
              <Typography variant="subtitle1" fontWeight={700}>{role.name}</Typography>
              <Typography variant="caption" color="text.secondary">{role.slug}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {role.permissions.includes('*') ? 'Full access' : `${role.permissions.length} privileges`}
              </Typography>
              <Box mt={1.5} display="flex" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedRoleId(role._id);
                    setInlinePermissions(role.permissions || []);
                    setEditing(role as unknown as Record<string, unknown>);
                    setForm({
                      name: role.name,
                      slug: role.slug,
                      description: '',
                      permissions: role.permissions.join(', '),
                    });
                    setOpen(true);
                  }}
                >
                  Edit Role
                </Button>
                {!role.isSystem && (
                  <Button size="small" color="error" onClick={() => deleteMutation.mutate(role._id)}>
                    Deactivate
                  </Button>
                )}
              </Box>
              <Button size="small" sx={{ mt: 1 }} onClick={() => { setSelectedRoleId(role._id); setInlinePermissions(role.permissions || []); }}>
                Manage Privileges
              </Button>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Privilege Editor — {selectedRole?.name || 'Select role'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Transport-style flow: select role, toggle grouped privileges, save.
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => inlineSaveMutation.mutate()} disabled={!selectedRoleId || inlineSaveMutation.isPending}>
            Save Privileges
          </Button>
        </Stack>
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label="Search privilege"
          value={permissionSearch}
          onChange={(e) => setPermissionSearch(e.target.value)}
        />
        <Box mt={1} maxHeight={330} overflow="auto">
          {filteredGroups.map((g) => {
            const checkedCount = g.permissions.filter((p) => inlinePermissions.includes(p.key)).length;
            return (
              <Accordion key={`inline-${g.group}`} disableGutters sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: '10px !important' }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack direction="row" justifyContent="space-between" width="100%">
                    <Typography variant="caption" fontWeight={700}>{g.group}</Typography>
                    <Chip size="small" label={`${checkedCount}/${g.permissions.length}`} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {g.permissions.map((perm) => {
                      const active = inlinePermissions.includes(perm.key);
                      return (
                        <Chip
                          key={`inline-chip-${perm.key}`}
                          clickable
                          size="small"
                          color={active ? 'primary' : 'default'}
                          variant={active ? 'filled' : 'outlined'}
                          label={perm.label}
                          onClick={() => {
                            setInlinePermissions((prev) => active ? prev.filter((k) => k !== perm.key) : [...prev, perm.key]);
                          }}
                        />
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>
      {showAdvanced && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {PLATFORM_ROLES.map((r) => (
              <Grid item xs={12} sm={6} md={4} key={r.slug}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{r.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.slug}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>{r.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <SerialNoDataGrid rows={data?.data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight
            sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
          <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={2} bgcolor="background.paper">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>Permissions Master</Typography>
              <Button size="small" variant="contained" onClick={() => { setPermEditing(null); setPermOpen(true); }}>
                Add Permission
              </Button>
            </Stack>
            <SerialNoDataGrid
              rows={permissionsData}
              getRowId={(r) => r.key}
              autoHeight
              columns={[
                { field: 'module', headerName: 'Module', width: 130 },
                { field: 'resource', headerName: 'Resource', width: 130 },
                { field: 'action', headerName: 'Action', width: 100 },
                { field: 'key', headerName: 'Permission Key', flex: 1 },
                {
                  field: 'actions', headerName: 'Actions', width: 100, sortable: false,
                  renderCell: (params) => (
                    <RowActions
                      onEdit={() => {
                        setPermEditing(params.row);
                        setPermForm({
                          module: params.row.module,
                          resource: params.row.resource,
                          action: params.row.action,
                          description: params.row.description || '',
                        });
                        setPermOpen(true);
                      }}
                      onDelete={() => params.row._id && deletePermissionMutation.mutate(params.row._id)}
                    />
                  ),
                },
              ] as GridColDef[]}
            />
          </Box>
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Role Privileges' : 'Create Role & Privileges'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Role Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth margin="normal" label="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })} />
          <TextField fullWidth margin="normal" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="subtitle2" fontWeight={700}>Privilege Matrix</Typography>
            <Chip label={`${selectedPermissions.size} selected`} size="small" color="primary" />
          </Stack>
          <TextField
            fullWidth
            size="small"
            margin="normal"
            label="Search privilege"
            placeholder="partners.view, documents.verify, settings..."
            value={permissionSearch}
            onChange={(e) => setPermissionSearch(e.target.value)}
          />
          <Box mt={1} maxHeight={340} overflow="auto">
            {filteredGroups.map((g) => {
              const checkedCount = g.permissions.filter((p) => selectedPermissions.has(p.key)).length;
              const allChecked = checkedCount === g.permissions.length && g.permissions.length > 0;
              return (
                <Accordion key={g.group} disableGutters sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: '10px !important' }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Stack direction="row" alignItems="center" spacing={1} width="100%" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={800} sx={{ textTransform: 'uppercase' }}>{g.group}</Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Chip size="small" label={`${checkedCount}/${g.permissions.length}`} />
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = new Set(selectedPermissions);
                            if (allChecked) g.permissions.forEach((p) => next.delete(p.key));
                            else g.permissions.forEach((p) => next.add(p.key));
                            setForm({ ...form, permissions: Array.from(next).join(', ') });
                          }}
                        >
                          {allChecked ? 'Clear' : 'All'}
                        </Button>
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {g.permissions.map((perm) => (
                        <Chip
                          key={perm.key}
                          clickable
                          size="small"
                          color={selectedPermissions.has(perm.key) ? 'primary' : 'default'}
                          variant={selectedPermissions.has(perm.key) ? 'filled' : 'outlined'}
                          label={`${perm.label} · ${perm.key.split('.').pop()}`}
                          onClick={() => {
                            const next = new Set(selectedPermissions);
                            if (next.has(perm.key)) next.delete(perm.key);
                            else next.add(perm.key);
                            setForm({ ...form, permissions: Array.from(next).join(', ') });
                          }}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.name || !form.slug || saveMutation.isPending}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={permOpen} onClose={() => setPermOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{permEditing ? 'Edit Permission' : 'Create Permission'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} mt={0.5}>
            <Grid item xs={4}><TextField fullWidth size="small" label="Module" value={permForm.module} onChange={(e) => setPermForm({ ...permForm, module: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" label="Resource" value={permForm.resource} onChange={(e) => setPermForm({ ...permForm, resource: e.target.value })} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Action" value={permForm.action} onChange={(e) => setPermForm({ ...permForm, action: e.target.value })}>
                {['create', 'read', 'update', 'delete', 'manage', 'approve', 'verify', 'export'].map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={permForm.description} onChange={(e) => setPermForm({ ...permForm, description: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => savePermissionMutation.mutate()} disabled={!permForm.module || !permForm.resource || !permForm.action}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState('');
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['report', selectedReport],
    queryFn: async () => {
      const res = await api.get(`/reports/${selectedReport}/run`);
      return res.data;
    },
    enabled: !!selectedReport,
  });

  const reports = [
    { slug: 'rides-summary', name: 'Rides Summary' },
    { slug: 'orders-summary', name: 'Orders Summary' },
    { slug: 'revenue-summary', name: 'Revenue Summary' },
    { slug: 'driver-performance', name: 'Driver Performance' },
    { slug: 'wallet-transactions', name: 'Wallet Transactions' },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Reports</Typography>
      <Grid container spacing={2} mb={3}>
        {reports.map((r) => (
          <Grid item xs={12} sm={6} md={4} key={r.slug}>
            <Button
              fullWidth variant={selectedReport === r.slug ? 'contained' : 'outlined'}
              onClick={() => { setSelectedReport(r.slug); refetch(); }}
              sx={{ py: 2 }}
            >
              {r.name}
            </Button>
          </Grid>
        ))}
      </Grid>
      {selectedReport && (
        <Box bgcolor="background.paper" p={2} borderRadius={2}>
          <Typography variant="subtitle1" gutterBottom>{selectedReport} Results</Typography>
          <pre style={{ overflow: 'auto', maxHeight: 400, fontSize: 12 }}>
            {isFetching ? 'Loading...' : JSON.stringify(data?.data, null, 2)}
          </pre>
        </Box>
      )}
    </Box>
  );
}


export { default as AuditLogsPage } from './AuditLogsPage';


export function WalletPage() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['my-wallet'],
    queryFn: () => import('../../services/walletService').then((m) => m.fetchMyWallet()),
    refetchOnWindowFocus: true,
  });

  const balance = data?.balance ?? 0;
  const transactions = data?.transactions ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Wallet"
        subtitle="Live balance and recent activity from your wallet account"
        actions={(
          <Button size="small" variant="outlined" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        )}
      />
      {isError && (
        <Typography color="error" sx={{ mb: 2 }}>Unable to load wallet. Try refresh.</Typography>
      )}
      <Box bgcolor="background.paper" p={3} borderRadius={2} mb={3}>
        <Typography variant="body2" color="text.secondary">Current balance</Typography>
        <Typography variant="h3" fontWeight={700}>
          {isLoading ? '…' : formatMoney(balance)}
        </Typography>
        {data?.currency && (
          <Typography variant="caption" color="text.secondary">{data.currency}</Typography>
        )}
        {data?.isFrozen && (
          <Chip size="small" color="warning" label="Wallet frozen" sx={{ mt: 1, display: 'block', width: 'fit-content' }} />
        )}
      </Box>
      <Typography variant="h6" gutterBottom>Recent transactions</Typography>
      {!isLoading && transactions.length === 0 && (
        <Typography color="text.secondary">No wallet activity yet.</Typography>
      )}
      {transactions.map((tx) => (
        <Box key={tx._id} display="flex" justifyContent="space-between" p={2} mb={1} bgcolor="background.paper" borderRadius={2}>
          <Box>
            <Typography fontWeight={600}>{tx.type}</Typography>
            <Typography variant="caption" display="block">{tx.description || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : ''}
            </Typography>
          </Box>
          <Typography color={tx.amount >= 0 ? 'success.main' : 'error.main'} fontWeight={600}>
            {tx.amount >= 0 ? '+' : '−'}{formatMoney(Math.abs(tx.amount))}
          </Typography>
        </Box>
      ))}
    </PageShell>
  );
}

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await api.get('/notifications');
      return res.data;
    },
  });

  const markReadMut = useMutation({
    mutationFn: (id: string) => api.put(`/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    },
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Notifications</Typography>
      {(data?.data || []).map((n: { _id: string; title: string; body: string; status: string; createdAt: string }) => (
        <Box key={n._id} p={2} mb={1} bgcolor="background.paper" borderRadius={2}
          borderLeft={4} borderColor={n.status === 'read' ? 'grey.300' : 'primary.main'}>
          <Typography fontWeight={600}>{n.title}</Typography>
          <Typography variant="body2" color="text.secondary">{n.body}</Typography>
          <Typography variant="caption">{new Date(n.createdAt).toLocaleString()}</Typography>
          {n.status !== 'read' && (
            <Box mt={1}>
              <Button size="small" onClick={() => markReadMut.mutate(n._id)} disabled={markReadMut.isPending}>
                Mark as read
              </Button>
            </Box>
          )}
        </Box>
      ))}
      {!isLoading && !(data?.data?.length) && (
        <Typography color="text.secondary">No notifications yet</Typography>
      )}
    </Box>
  );
}

export { default as PaymentGatewaysPage } from './PaymentGatewaysPage';

export { default as SmsGatewaysPage } from './SmsGatewaysPage';

export { default as TranslationsPage } from './TranslatorMasterPage';

export function PromosPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: 10,
    minOrderAmount: 0,
    usageLimit: 100,
    maxUsesPerUser: 1,
    modules: [] as string[],
    zoneIds: [] as string[],
    serviceIds: [] as string[],
    customerIds: [] as string[],
    validFrom: new Date().toISOString().slice(0, 10),
    validTo: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    isActive: true,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['promos'],
    queryFn: async () => {
      const res = await api.get('/admin/promos');
      return res.data;
    },
  });
  const { data: servicesRes } = useQuery({
    queryKey: ['promo-services'],
    queryFn: async () => {
      const res = await api.get('/admin/services');
      return res.data.data as { _id: string; name: string }[];
    },
  });
  const { data: zonesRes } = useQuery({
    queryKey: ['promo-zones'],
    queryFn: async () => {
      const res = await api.get('/zones', { params: { limit: 200 } });
      return res.data.data as { _id: string; name: string }[];
    },
  });
  const { data: usersRes } = useQuery({
    queryKey: ['promo-users'],
    queryFn: async () => {
      const res = await api.get('/admin/users', { params: { limit: 200 } });
      return res.data.data as { _id: string; firstName: string; lastName?: string }[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        validFrom: new Date(form.validFrom),
        validTo: new Date(form.validTo),
      };
      if (editing?._id) return api.put(`/admin/promos/${editing._id as string}`, payload);
      return api.post('/admin/promos', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promos'] });
      setOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/promos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['promos'] }),
  });

  const columns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 120 },
    { field: 'discountType', headerName: 'Type', width: 100 },
    { field: 'discountValue', headerName: 'Value', width: 80 },
    { field: 'usedCount', headerName: 'Used', width: 80 },
    { field: 'usageLimit', headerName: 'Limit', width: 80 },
    {
      field: 'validTo', headerName: 'End Date', width: 120,
      valueFormatter: (v) => new Date(v as string).toLocaleDateString(),
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      valueGetter: (_v, row) => {
        const expired = new Date(row.validTo) < new Date();
        if (expired) return 'Expired';
        return row.isActive ? 'Active' : 'Inactive';
      },
    },
    {
      field: 'isActive', headerName: 'Active', width: 80,
      renderCell: (params) => <Switch checked={params.value as boolean} size="small" disabled />,
    },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (params) => (
        <RowActions
          onEdit={() => {
            setEditing(params.row);
            setForm({
              code: params.row.code,
              description: params.row.description || '',
              discountType: params.row.discountType || 'percentage',
              discountValue: params.row.discountValue || 0,
              minOrderAmount: params.row.minOrderAmount || 0,
              usageLimit: params.row.usageLimit || 0,
              maxUsesPerUser: params.row.maxUsesPerUser || 1,
              modules: params.row.modules || [],
              zoneIds: (params.row.zoneIds || []).map((z: { _id?: string } | string) => typeof z === 'string' ? z : z._id || ''),
              serviceIds: (params.row.serviceIds || []).map((s: { _id?: string } | string) => typeof s === 'string' ? s : s._id || ''),
              customerIds: (params.row.customerIds || []).map((u: { _id?: string } | string) => typeof u === 'string' ? u : u._id || ''),
              validFrom: new Date(params.row.validFrom).toISOString().slice(0, 10),
              validTo: new Date(params.row.validTo).toISOString().slice(0, 10),
              isActive: params.row.isActive !== false,
            });
            setOpen(true);
          }}
          onDelete={() => deleteMutation.mutate(params.row._id)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={700}>Promo Codes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setOpen(true); }}>
          Add Promo
        </Button>
      </Box>
      <SerialNoDataGrid rows={data?.data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editing} />
          <TextField fullWidth margin="normal" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <TextField select fullWidth margin="normal" label="Discount Type" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
            <MenuItem value="percentage">Percentage</MenuItem>
            <MenuItem value="fixed">Fixed</MenuItem>
          </TextField>
          <Grid container spacing={1}>
            <Grid item xs={6}><TextField fullWidth margin="normal" type="number" label="Discount Value" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: Number(e.target.value) })} /></Grid>
            <Grid item xs={6}><TextField fullWidth margin="normal" type="number" label="Usage Limit" value={form.usageLimit} onChange={(e) => setForm({ ...form, usageLimit: Number(e.target.value) })} /></Grid>
            <Grid item xs={6}><TextField fullWidth margin="normal" type="number" label="Max uses / user" value={form.maxUsesPerUser} onChange={(e) => setForm({ ...form, maxUsesPerUser: Number(e.target.value) })} /></Grid>
            <Grid item xs={6}><TextField fullWidth margin="normal" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth margin="normal" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth margin="normal" label="Service-wise modules" value={form.modules} SelectProps={{ multiple: true }} onChange={(e) => setForm({ ...form, modules: e.target.value as unknown as string[] })}>
                {['transportation', 'food', 'grocery'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth margin="normal" label="Specific services" value={form.serviceIds} SelectProps={{ multiple: true }} onChange={(e) => setForm({ ...form, serviceIds: e.target.value as unknown as string[] })}>
                {(servicesRes || []).map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth margin="normal" label="Zone-wise scope" value={form.zoneIds} SelectProps={{ multiple: true }} onChange={(e) => setForm({ ...form, zoneIds: e.target.value as unknown as string[] })}>
                {(zonesRes || []).map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={usersRes || []}
                getOptionLabel={(u) => `${u.firstName} ${u.lastName || ''}`.trim()}
                value={(usersRes || []).filter((u) => form.customerIds.includes(u._id))}
                onChange={(_, selected) => setForm({ ...form, customerIds: selected.map((u) => u._id) })}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField {...params} margin="normal" label="Individual users (search & multiselect)" placeholder="Type name to search…" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option._id} label={`${option.firstName} ${option.lastName || ''}`.trim()} size="small" />
                  ))
                }
              />
            </Grid>
          </Grid>
          <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={!form.code || !form.validTo || !form.validFrom || new Date(form.validTo) < new Date(form.validFrom)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
